import { Suspense, lazy, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { FormField, Input, Select, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { GATILHOS } from '@/data/automacoesDaClinica';
import { CAMPOS_POR_GATILHO, ID_DO_GATILHO, PASSOS, validarFluxo } from '@/data/fluxos';
import type { PassoDoFluxo, TipoDePasso } from '@/data/fluxos';
import { dados } from '@/data/porta';
import type { DadosDoFluxo, Fluxo, GatilhoDeAutomacao, PropostaDoAgente, RascunhoDoAgente, SimulacaoDoFluxo } from '@/data/tipos';
import { useEscuro } from '@/tema';
import { EditorDePasso } from './EditorDePasso';

/* O canvas carrega sob demanda: a biblioteca de desenho só entra no pacote de
   quem abre o construtor, e não no de quem só quer ver a agenda. */
const CanvasDeFluxo = lazy(() => import('./CanvasDeFluxo'));

const NOVO: DadosDoFluxo = {
    nome: '',
    descricao: '',
    gatilho: 'sem-retorno-90d',
    passos: [],
    ativo: false,
};

function proximoId(passos: PassoDoFluxo[]): string {
    return `p${passos.length + 1}-${Date.now().toString().slice(-4)}`;
}

function passoNovo(tipo: TipoDePasso, passos: PassoDoFluxo[], gatilho: GatilhoDeAutomacao): PassoDoFluxo {
    const id = proximoId(passos);
    if (tipo === 'condicao') {
        return {
            id, tipo: 'condicao',
            campo: CAMPOS_POR_GATILHO[gatilho]?.[0]?.campo ?? 'nome',
            operador: 'maior', valor: '',
        };
    }
    if (tipo === 'acao') return { id, tipo: 'acao', acao: 'followup_criar', campos: { titulo: '' } };
    if (tipo === 'agente') return { id, tipo: 'agente', instrucao: '' };
    return { id, tipo: 'espera', dias: 3 };
}

/**
 * O construtor de fluxos.
 *
 * O desenho é um canvas de verdade (React Flow), como o n8n: gatilho no topo,
 * passos ligados por seta, posição livre. Mas **o desenho não é o modelo** — o
 * que executa continua sendo uma sequência, e por isso ligar um passo no outro
 * reordena em vez de criar ramo, e não há aresta para apagar. Um passo solto no
 * quadro seria um passo que o motor nunca saberia quando rodar.
 *
 * O arrastar não é o único caminho: o passo selecionado abre no painel abaixo,
 * com rótulo, dica e os botões de subir e descer. Quem não pode arrastar ainda
 * monta o fluxo inteiro pelo teclado.
 *
 * A simulação é o que separa este construtor de um formulário: ela roda o
 * gatilho de verdade, aplica as condições e mostra quem sobra — antes de
 * qualquer coisa acontecer.
 */
export function ConstrutorDeFluxos({ fluxos, disponivel }: { fluxos: Fluxo[]; disponivel: boolean }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<Fluxo | 'novo' | null>(null);
    const [form, setForm] = useState<DadosDoFluxo>(NOVO);
    /* O canvas inteiro estava a um Esc de sumir. */
    const formSujo = useAlteracaoPendente(form, emEdicao);
    const [confirmar, confirmacao] = useConfirmacao();
    const [salvando, setSalvando] = useState(false);
    const [gerando, setGerando] = useState(false);
    const [descricaoIa, setDescricaoIa] = useState('');
    const [simulacao, setSimulacao] = useState<{ fluxo: Fluxo; resultado: SimulacaoDoFluxo } | null>(null);
    /* O que o passo de agente redigiu na última execução. Cada proposta só
       acontece quando alguém confirma, com os campos à vista (regra 8). */
    const [rascunhos, setRascunhos] = useState<RascunhoDoAgente[]>([]);
    const [confirmando, setConfirmando] = useState<string | null>(null);
    const [executando, setExecutando] = useState<string | null>(null);
    const [selecionado, setSelecionado] = useState<string | null>(null);
    const escuro = useEscuro();

    const passos = (form.passos ?? []) as PassoDoFluxo[];
    const problemas = validarFluxo(form.gatilho, passos);

    const abrir = (fluxo: Fluxo | 'novo') => {
        setEmEdicao(fluxo);
        setSelecionado(null);
        setForm(fluxo === 'novo'
            ? NOVO
            : {
                nome: fluxo.nome, descricao: fluxo.descricao, gatilho: fluxo.gatilho,
                passos: JSON.parse(JSON.stringify(fluxo.passos)), ativo: fluxo.ativo,
            });
    };

    const trocarPasso = (id: string, mudanca: Partial<PassoDoFluxo>) => {
        setForm({
            ...form,
            passos: passos.map((p) => (p.id === id ? { ...p, ...mudanca } as PassoDoFluxo : p)),
        });
    };

    const mover = (indice: number, direcao: -1 | 1) => {
        const destino = indice + direcao;
        if (destino < 0 || destino >= passos.length) return;
        const copia = [...passos];
        const [movido] = copia.splice(indice, 1);
        if (movido) copia.splice(destino, 0, movido);
        setForm({ ...form, passos: copia });
    };

    const adicionar = (tipo: TipoDePasso) => {
        const passo = passoNovo(tipo, passos, form.gatilho);
        setForm({ ...form, passos: [...passos, passo] });
        /* Quem acabou de criar o passo quer preenchê-lo: o painel já abre nele. */
        setSelecionado(passo.id);
    };

    const indiceSelecionado = passos.findIndex((p) => p.id === selecionado);
    const passoSelecionado = indiceSelecionado >= 0 ? passos[indiceSelecionado] : undefined;

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarFluxo(emEdicao === 'novo' ? null : emEdicao.id, form);
            aviso({ title: emEdicao === 'novo' ? 'Fluxo criado' : 'Fluxo salvo', description: form.nome });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const gerar = async () => {
        setGerando(true);
        try {
            const rascunho = await dados().gerarFluxoComIa(descricaoIa);
            setForm(rascunho);
            setEmEdicao('novo');
            setDescricaoIa('');
            aviso({
                title: 'Rascunho pronto',
                description: 'Confira os passos antes de salvar — ele nasce desligado.',
            });
        } catch (e) {
            aviso({ title: 'Não foi possível gerar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setGerando(false);
        }
    };

    const simular = async (fluxo: Fluxo) => {
        try {
            const resultado = await dados().simularFluxo(fluxo.id);
            setSimulacao({ fluxo, resultado });
        } catch (e) {
            aviso({ title: 'Não foi possível simular', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const executar = async (fluxo: Fluxo) => {
        setExecutando(fluxo.id);
        try {
            const r = await dados().executarFluxo(fluxo.id);
            const fezAlgo = r.criadas > 0 || r.rascunhos.length > 0;
            aviso({
                title: fezAlgo ? r.descricao : 'Nada foi executado',
                /* Sem nada feito, o motivo é a descrição: "nada foi executado"
                   sozinho manda a clínica tentar de novo sem saber o que falta. */
                description: fezAlgo ? (r.repetidas ? `${r.repetidas} não puderam rodar` : fluxo.nome) : r.descricao,
                tone: fezAlgo ? 'success' : 'info',
            });
            setSimulacao(null);
            setRascunhos(r.rascunhos);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível executar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setExecutando(null);
        }
    };

    /* Confirmar passa pelo mesmo caminho da tela — validação e RLS iguais. O
       nome do caso entra no follow-up aqui, na clínica: ele não subiu ao
       provedor de IA. */
    const confirmarProposta = async (rascunho: RascunhoDoAgente, proposta: PropostaDoAgente, chave: string) => {
        setConfirmando(chave);
        try {
            const campos = proposta.ferramenta === 'followup_criar' && !proposta.campos.paciente
                ? { ...proposta.campos, paciente: rascunho.caso }
                : proposta.campos;
            const r = await dados().confirmarPropostaDoAgente({ ...proposta, campos });
            aviso({ title: 'Proposta confirmada', description: r.descricao, tone: 'success' });
            setRascunhos((atuais) => atuais.map((x) => (x === rascunho ? { ...x, propostas: x.propostas.filter((p) => p !== proposta) } : x)));
        } catch (e) {
            aviso({ title: 'Não foi possível confirmar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setConfirmando(null);
        }
    };

    const remover = async (fluxo: Fluxo) => {
        const ok = await confirmar({
            titulo: `Remover o fluxo ${fluxo.nome}?`,
            descricao: 'Os passos desenhados somem, e não voltam.',
            confirmar: 'Remover fluxo',
        });
        if (!ok) return;
        try {
            await dados().removerFluxo(fluxo.id);
            aviso({ title: 'Fluxo removido', description: fluxo.nome });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card title="Descreva o fluxo e a IA monta o rascunho" padding={18}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <FormField
                        label="O que deve acontecer" htmlFor="fluxo-ia" style={{ flex: '1 1 280px', minWidth: 0 }}
                        hint="Uma frase basta. O rascunho vem desligado, para você conferir antes."
                    >
                        <Input
                            id="fluxo-ia" value={descricaoIa} onChange={setDescricaoIa}
                            placeholder="Quem sumiu há mais de 4 meses vira tarefa para a recepção ligar"
                        />
                    </FormField>
                    <Button
                        onClick={gerar}
                        disabled={gerando || descricaoIa.trim().length < 10 || !disponivel}
                        iconLeft={<Icon name="sparkles" size={15} />}
                    >
                        {gerando ? 'Montando…' : 'Montar rascunho'}
                    </Button>
                </div>
                {!disponivel && (
                    <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        Montar com IA depende de uma chave configurada na plataforma. Sem ela, monte à mão — o
                        construtor abaixo funciona igual.
                    </p>
                )}
            </Card>

            <Card
                title="Fluxos"
                subtitle="Gatilho, condições e ações em ordem"
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('novo')}>
                        Novo fluxo
                    </Button>
                )}
            >
                {fluxos.length === 0 ? (
                    <EmptyState
                        icon="workflow"
                        title="Nenhum fluxo ainda"
                        description="Comece pelo mais pedido: quem sumiu vira tarefa para alguém ligar."
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {fluxos.map((f) => (
                            <div key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: 16, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
                                <span
                                    aria-hidden
                                    style={{
                                        width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)',
                                        background: f.ativo ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                                        color: f.ativo ? 'var(--text-brand)' : 'var(--neutral-500)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <Icon name="workflow" size={16} />
                                </span>

                                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                                        <strong style={{ fontSize: 14, fontWeight: 600 }}>{f.nome}</strong>
                                        <Badge tone={f.ativo ? 'success' : 'neutral'} dot>{f.ativo ? 'Ativo' : 'Desligado'}</Badge>
                                        <Badge tone="info">{GATILHOS[f.gatilho]?.rotulo ?? f.gatilho}</Badge>
                                        <Badge tone="neutral">{(f.passos ?? []).length} passos</Badge>
                                    </div>
                                    <p style={{ margin: '7px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                        {f.descricao || 'Sem descrição.'}
                                    </p>
                                    <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {f.execucoes === 0 ? 'nunca executou' : `${f.execucoes} execuções`}
                                    </p>
                                </div>

                                <div style={{ display: 'flex', gap: 6 }}>
                                    <Button size="sm" variant="secondary" onClick={() => simular(f)}>Simular</Button>
                                    <Button size="sm" variant="ghost" onClick={() => abrir(f)}>Editar</Button>
                                    <Button size="sm" variant="ghost" onClick={() => remover(f)}>Remover</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {confirmacao}

            {/* ----------------------------------------------- construtor --- */}
            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo fluxo' : 'Editar fluxo'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                width={900}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={!form.nome.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(220px,100%),1fr))', gap: 14 }}>
                        <FormField label="Nome" htmlFor="fl-nome" required>
                            <Input id="fl-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} />
                        </FormField>
                        <FormField label="Gatilho" htmlFor="fl-gatilho" hint={GATILHOS[form.gatilho]?.descricao}>
                            <Select
                                id="fl-gatilho" value={form.gatilho}
                                onChange={(v) => setForm({ ...form, gatilho: v as GatilhoDeAutomacao })}
                                options={Object.entries(GATILHOS).map(([id, g]) => ({ value: id, label: g.rotulo }))}
                            />
                        </FormField>
                        <FormField label="Descrição" htmlFor="fl-descricao" style={{ gridColumn: '1 / -1' }}>
                            <Textarea id="fl-descricao" rows={2} value={form.descricao} onChange={(v) => setForm({ ...form, descricao: v })} />
                        </FormField>
                    </div>

                    {/* O desenho: gatilho no topo, passos ligados na ordem em que rodam. */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>Desenho do fluxo</span>
                            <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                Arraste para posicionar. Ligue a saída de um passo na entrada de outro para mudar a ordem.
                            </span>
                        </div>

                        <Suspense
                            fallback={(
                                <div
                                    style={{
                                        height: 380, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 13, color: 'var(--text-secondary)', background: 'var(--surface-secondary)',
                                    }}
                                >
                                    Carregando o desenho…
                                </div>
                            )}
                        >
                            <CanvasDeFluxo
                                gatilho={form.gatilho}
                                passos={passos}
                                selecionado={selecionado}
                                onSelecionar={setSelecionado}
                                onMudarPassos={(novos) => setForm({ ...form, passos: novos })}
                                onAviso={(mensagem) => aviso({ title: mensagem, tone: 'info' })}
                                escuro={escuro}
                            />
                        </Suspense>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                            {PASSOS.map((p) => (
                                <Button
                                    key={p.tipo} size="sm" variant="secondary"
                                    iconLeft={<Icon name={p.icone} size={14} />}
                                    onClick={() => adicionar(p.tipo)}
                                >
                                    {p.rotulo}
                                </Button>
                            ))}
                        </div>

                        {/* O painel de propriedades. Fora do quadro porque cartão de
                            220px não comporta rótulo, dica e teclado. */}
                        <div style={{ marginTop: 12 }}>
                            {passoSelecionado ? (
                                <EditorDePasso
                                    passo={passoSelecionado}
                                    gatilho={form.gatilho}
                                    indice={indiceSelecionado}
                                    total={passos.length}
                                    onMudar={(mudanca) => trocarPasso(passoSelecionado.id, mudanca)}
                                    onMover={(direcao) => mover(indiceSelecionado, direcao)}
                                    onRemover={() => {
                                        setForm({ ...form, passos: passos.filter((x) => x.id !== passoSelecionado.id) });
                                        setSelecionado(null);
                                    }}
                                />
                            ) : (
                                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    {selecionado === ID_DO_GATILHO
                                        ? `Quando: ${GATILHOS[form.gatilho]?.rotulo ?? form.gatilho} · ${GATILHOS[form.gatilho]?.alcanceRotulo ?? ''}. O gatilho se troca no campo acima.`
                                        : passos.length === 0
                                            ? 'Nenhum passo ainda. Comece por uma condição ou vá direto à ação.'
                                            : 'Clique num passo do desenho para editar o que ele faz.'}
                                </p>
                            )}
                        </div>
                    </div>

                    {problemas.length > 0 && (
                        /* O fluxo pode ser salvo com problema — quem monta às
                           vezes para no meio. O que não pode é ser ativado sem
                           saber, e por isso os problemas ficam à vista. */
                        <Alert tone="warning" dismissible={false} title="Este fluxo ainda não está pronto para rodar">
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                {problemas.map((p) => <li key={p.mensagem}>{p.mensagem}</li>)}
                            </ul>
                        </Alert>
                    )}
                </div>
            </Modal>

            {/* ------------------------------------------------ simulação --- */}
            <Modal
                open={simulacao !== null}
                title={simulacao ? `Simulação — ${simulacao.fluxo.nome}` : 'Simulação'}
                onClose={() => setSimulacao(null)}
                width={680}
                footer={simulacao && (
                    <>
                        <Button variant="ghost" onClick={() => setSimulacao(null)}>Fechar</Button>
                        <Button
                            onClick={() => executar(simulacao.fluxo)}
                            /* O que decide não é "sobrou alguém no fim": uma ação
                               antes da condição alcança quem a condição barraria
                               depois. Se nenhuma ação alcança ninguém, não há o
                               que executar. */
                            disabled={executando === simulacao.fluxo.id || simulacao.resultado.acoes.every((a) => a.quantos === 0)}
                        >
                            {executando === simulacao.fluxo.id ? 'Executando…' : 'Executar agora'}
                        </Button>
                    </>
                )}
            >
                {simulacao && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                            O gatilho alcança <strong>{simulacao.resultado.alcanceBruto}</strong>;{' '}
                            <strong>{simulacao.resultado.casos.filter((c) => c.alcancado).length}</strong> passam pelas
                            condições. Nada foi escrito — isto é o que aconteceria.
                        </p>

                        {simulacao.resultado.problemas.map((p) => (
                            <Alert key={p.mensagem} tone="warning" dismissible={false}>{p.mensagem}</Alert>
                        ))}

                        <div>
                            <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                Quem o fluxo alcança
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                                {simulacao.resultado.casos.length === 0 && (
                                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                        Ninguém agora. O gatilho não encontrou casos nesta clínica.
                                    </span>
                                )}
                                {simulacao.resultado.casos.map((c) => (
                                    <div
                                        key={c.rotulo}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                                            padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                                            border: '1px solid var(--border)',
                                            opacity: c.alcancado ? 1 : 0.6,
                                        }}
                                    >
                                        <Icon name={c.alcancado ? 'circle-check' : 'circle-slash'} size={14} />
                                        <strong style={{ fontWeight: 600 }}>{c.rotulo}</strong>
                                        <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                                            {c.motivo ?? Object.entries(c.campos).slice(0, 2).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                O que aconteceria
                            </span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                {simulacao.resultado.acoes.length === 0 && (
                                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Este fluxo não tem ação.</span>
                                )}
                                {simulacao.resultado.acoes.map((a) => (
                                    <div key={a.passo} style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                            <Icon name="zap" size={14} />
                                            <strong style={{ fontWeight: 600 }}>{a.rotulo}</strong>
                                            <Badge tone={a.bloqueio ? 'warning' : 'success'}>
                                                {a.bloqueio ? 'não roda' : `${a.quantos}×`}
                                            </Badge>
                                        </div>
                                        {a.bloqueio && (
                                            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>{a.bloqueio}</p>
                                        )}
                                        {a.exemplo && (
                                            <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                                Exemplo: {Object.entries(a.exemplo).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={rascunhos.length > 0}
                onClose={() => setRascunhos([])}
                width={680}
                title="Rascunhos do agente"
                description="Nada disto aconteceu ainda. Confira o texto e confirme só as propostas que devem acontecer."
                footer={<Button variant="ghost" onClick={() => setRascunhos([])}>Fechar</Button>}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {rascunhos.map((rascunho, i) => (
                        <div
                            key={`${rascunho.passo}-${rascunho.caso}-${i}`}
                            style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', display: 'flex', flexDirection: 'column', gap: 8 }}
                        >
                            <strong style={{ fontSize: 13, fontWeight: 600 }}>{rascunho.caso}</strong>
                            {/* O nome volta aqui, na clínica: o provedor escreveu {nome}. */}
                            <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap', color: 'var(--text-body)' }}>
                                {rascunho.texto.replace(/\{nome\}/g, rascunho.caso)}
                            </p>
                            {rascunho.propostas.length === 0 && (
                                <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>Sem ação proposta.</span>
                            )}
                            {rascunho.propostas.map((proposta, j) => {
                                const chave = `${i}-${j}`;
                                return (
                                    <div key={chave} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <Badge tone="info">{proposta.rotulo}</Badge>
                                        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: '1 1 200px', minWidth: 0 }}>
                                            {Object.entries(proposta.campos).map(([campo, valor]) => `${campo}: ${String(valor)}`).join(' · ')}
                                        </span>
                                        <Button size="sm" loading={confirmando === chave} onClick={() => void confirmarProposta(rascunho, proposta, chave)}>
                                            Confirmar
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
}
